"use client";

// ── The editor toolbar's controls (`EX-EDR01`) ───────────────────────────────
// The language and version as one control (they were two selects in
// `CodingTaskRuntimeSelector`, which the exam still uses), the "Aa" text
// settings, and a popover any surface can hang a tool in (the contest's Print
// control). All of them float over the editor and none of them joins the
// column's flow, which is the whole point of the redesign.

import { useMemo, type ReactNode } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronsUpDown, Minus, Plus } from "lucide-react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { FONT_SIZE_MAX, FONT_SIZE_MIN, clampFontSize, type EditorSettings } from "@/features/playground/lib/editor-settings";
import {
  formatRuntimeLanguageName,
  formatRuntimeVersionLabel,
} from "@/features/quiz/lib/coding-task-format";
import type { AllowedRuntime } from "./use-solution-draft";

const MENU_SURFACE =
  "z-[200] rounded-xl border border-white/10 bg-neutral-900 p-1 text-white shadow-card";

/**
 * One runtime in words: the version's own label, which already names the
 * language ("C++17", "Python 3.11", "Java 17"), so the name is not said twice.
 */
export function runtimeLabel(languageKey: string, languageVersion: string): string {
  return formatRuntimeVersionLabel(languageKey, languageVersion);
}

/**
 * Language and version in one menu: a heading per language, a row per version.
 * With a single language at a single version there is nothing to choose, so it
 * renders as a label and not as a button that opens a one-item menu.
 */
export function RuntimePicker({
  allowedRuntimes,
  languageKey,
  languageVersion,
  onChange,
}: {
  allowedRuntimes: AllowedRuntime[];
  languageKey: string;
  languageVersion: string;
  onChange: (next: AllowedRuntime) => void;
}) {
  const t = useTranslations("judge.editor");

  const byLanguage = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const runtime of allowedRuntimes) {
      const versions = map.get(runtime.languageKey) ?? [];
      versions.push(runtime.languageVersion);
      map.set(runtime.languageKey, versions);
    }
    return map;
  }, [allowedRuntimes]);

  const current = runtimeLabel(languageKey, languageVersion);

  if (allowedRuntimes.length <= 1) {
    return (
      <span className="inline-flex h-9 items-center rounded-lg bg-white/5 px-2.5 text-xs font-semibold text-white/80">
        {current}
      </span>
    );
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          type="button"
          variant="ghostDark"
          size="sm"
          aria-label={t("runtimeLabel", { runtime: current })}
          className="max-w-[14rem] shrink-0 bg-white/5"
        >
          <span className="truncate">{current}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-white/50" aria-hidden />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={cn(MENU_SURFACE, "max-h-[min(60vh,420px)] min-w-[15rem] overflow-y-auto")}
          sideOffset={6}
          align="start"
          collisionPadding={8}
        >
          {[...byLanguage.entries()].map(([key, versions]) => (
            <DropdownMenu.Group key={key}>
              <DropdownMenu.Label className="px-2.5 pb-1 pt-2 text-[11px] font-semibold text-white/45">
                {formatRuntimeLanguageName(key)}
              </DropdownMenu.Label>
              {versions.map((version) => {
                const selected = key === languageKey && version === languageVersion;
                return (
                  <DropdownMenu.Item
                    key={version}
                    onSelect={() => onChange({ languageKey: key, languageVersion: version })}
                    className="flex cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-white/90 outline-none data-[highlighted]:bg-white/10"
                  >
                    {selected ? (
                      <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                    ) : (
                      <span className="w-4 shrink-0" />
                    )}
                    {runtimeLabel(key, version)}
                  </DropdownMenu.Item>
                );
              })}
            </DropdownMenu.Group>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** A toolbar button that opens a small panel below it. */
export function ToolbarPopover({
  trigger,
  label,
  children,
  align = "end",
  flush = false,
  testId,
}: {
  trigger: ReactNode;
  label: string;
  children: ReactNode;
  align?: "start" | "end";
  /** The content brings its own card (the contest's Print control), so no second frame. */
  flush?: boolean;
  testId?: string;
}) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <Button
          type="button"
          variant="ghostDark"
          size="sm"
          aria-label={label}
          title={label}
          className="shrink-0"
          data-testid={testId}
        >
          {trigger}
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={cn(
            flush ? "z-[200] rounded-xl shadow-card" : cn(MENU_SURFACE, "p-3"),
            "w-[min(100vw-2rem,20rem)]",
          )}
          sideOffset={6}
          align={align}
          collisionPadding={8}
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** The "Aa" panel: font size, tab width 2 or 4, wrap long lines. */
export function TextSettingsPanel({
  settings,
  onChange,
}: {
  settings: EditorSettings;
  onChange: (next: EditorSettings) => void;
}) {
  const t = useTranslations("judge.editor");
  const step = (delta: number) => onChange({ ...settings, fontSize: clampFontSize(settings.fontSize + delta) });

  return (
    <div className="space-y-3 text-sm">
      <p className="text-xs font-semibold text-white/55">{t("settingsTitle")}</p>
      <div className="flex items-center justify-between gap-3">
        <span className="text-white/80">{t("fontSize")}</span>
        <span className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghostDark"
            size="smIcon"
            onClick={() => step(-1)}
            disabled={settings.fontSize <= FONT_SIZE_MIN}
            aria-label={t("fontSmaller")}
          >
            <Minus className="h-4 w-4" aria-hidden />
          </Button>
          <span className="w-8 text-center tabular-nums text-white" data-testid="editor-font-size">
            {t("fontSizeValue", { size: settings.fontSize })}
          </span>
          <Button
            type="button"
            variant="ghostDark"
            size="smIcon"
            onClick={() => step(1)}
            disabled={settings.fontSize >= FONT_SIZE_MAX}
            aria-label={t("fontLarger")}
          >
            <Plus className="h-4 w-4" aria-hidden />
          </Button>
        </span>
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-white/80">{t("tabWidth")}</span>
        <SegmentedControl
          label={t("tabWidth")}
          size="sm"
          tone="dark"
          options={[
            { value: "2", label: t("tabWidthOption", { n: 2 }) },
            { value: "4", label: t("tabWidthOption", { n: 4 }) },
          ]}
          value={settings.tabSize === 2 ? "2" : "4"}
          onChange={(value) => onChange({ ...settings, tabSize: value === "2" ? 2 : 4 })}
        />
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-white/80">{t("wrap")}</span>
        <SegmentedControl
          label={t("wrap")}
          size="sm"
          tone="dark"
          options={[
            { value: "off", label: t("wrapOff") },
            { value: "on", label: t("wrapOn") },
          ]}
          value={settings.wordWrap ? "on" : "off"}
          onChange={(value) => onChange({ ...settings, wordWrap: value === "on" })}
        />
      </div>
    </div>
  );
}
